import React, {useCallback, useState, useEffect, useRef, useMemo} from "react";
import {InjectedRouter} from "react-router/lib/Router";
import Toolbar from "common_components/toolbar";
import {Button, TextField, Tooltip, IconButton} from "@material-ui/core";
import {
  Description,
  InfoOutlined,
  Add,
  Close,
  ExpandMore,
} from "@material-ui/icons";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import FileUploader, {CombinedTypes} from "./file_uploader";
import {
  MatterTreeView,
  MatterContextButton,
  buildTreeStructure,
} from "common_components/matter";
import IconToggle from "common_components/icon_toggle";

import {
  FileItem,
  Matter,
  MatterDocument,
  MatterTreeNode,
  CollectionTypeNames,
} from "common/types/matter";
import {UserId} from "common/types/user";

const SUPPORTED_TYPES = ["MSA", "SOW", "MSOW"] as const;

function getNewDocumentType(filename) {
  if (filename.includes("MSA")) {
    return "MSA";
  }

  if (filename.includes("SOW")) {
    return "SOW";
  }

  return undefined;
}

interface MatterEditorProps {
  onAnalyse: (
    name: string,
    fileItems: FileItem[],
    collectionTypeNames: CollectionTypeNames,
    context?: string,
    clientContextId?: number | null,
    isProjectContextDisabled?: boolean,
    targetParentDocumentId?: number,
    removedMsaIds?: number[],
  ) => Promise<void>;
  matter?: Matter;
  inWordAddinView?: boolean;
  currentDocumentName?: string;
  hideNameField?: boolean;
  updateName?: (name: string) => void;
  updateContext?: (
    context: string,
    clientContextId: number | null,
    isProjectContextDisabled?: boolean,
  ) => void;
  canEditContext?: boolean;
  router?: InjectedRouter;
  params: {
    organisationId: string;
    projectId: string;
    matterId?: string;
  };
  hideFileUpload?: boolean;
  currentUserId?: UserId;
  canUploadMsow?: boolean;
}

const styles = {
  nameFieldContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "1em",
    marginBottom: ".5em",
  },
  nameFormatHint: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    margin: "2px 0 14px",
  },
  nameFormatLabel: {
    fontSize: "0.7rem",
    fontWeight: 500,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#757575",
  },
  nameFormatValue: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#424242",
  },
  analyseButtonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    marginTop: "2em",
  },
  messageBase: {
    marginTop: "1.25em",
    padding: "12px 20px",
    borderRadius: "4px",
    fontSize: "0.9rem",
    maxWidth: "500px",
    textAlign: "center",
  },
  infoMessage: {
    backgroundColor: "#f5f5f5",
    color: "#424242",
  },
  errorMessage: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontWeight: 500,
  },
  targetBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginTop: "1em",
    marginBottom: "0.5em",
    padding: "8px 12px",
    borderRadius: "4px",
    backgroundColor: "#e3f2fd",
    border: "1px solid #1976d2",
    color: "#1976d2",
    fontSize: "0.9rem",
    cursor: "pointer",
  },
  targetBannerHint: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    whiteSpace: "nowrap",
    fontSize: "0.8rem",
    opacity: 0.9,
  },
  msaBanner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "1em",
    marginBottom: "0.5em",
    padding: "8px 12px",
    borderRadius: "4px",
    backgroundColor: "#fff8e1",
    border: "1px solid #f9a825",
    color: "#8d6e00",
    fontSize: "0.9rem",
  },
} as const;

const MatterEditor: React.FC<MatterEditorProps> = ({
  onAnalyse,
  matter,
  inWordAddinView = false,
  currentDocumentName,
  hideNameField = false,
  updateName,
  updateContext,
  canEditContext = false,
  router,
  params,
  hideFileUpload = false,
  currentUserId,
  canUploadMsow = false,
}) => {
  const [name, setName] = useState<string>(matter?.config?.name ?? "");
  const [
    collectionTypeNames,
    setCollectionTypeNames,
  ] = useState<CollectionTypeNames>(
    Object.fromEntries(
      SUPPORTED_TYPES.map(type => [type, null]),
    ) as CollectionTypeNames,
  );

  useEffect(() => {
    if (matter?.config?.name) {
      setName(matter.config.name);
    } else {
      setName("");
    }
  }, [matter?.id, matter?.config?.name]);

  const hasNameChanged = name !== matter?.config?.name && name.trim() !== "";
  const isNew = (matter?.documents?.length ?? 0) === 0;

  const [fileItems, setFileItems] = useState<FileItem[]>(() => {
    if (inWordAddinView && currentDocumentName) {
      return [
        {
          file: {name: currentDocumentName} as File,
          documentType: isNew ? undefined : "SOW",
        },
      ];
    }
    return [];
  });
  const fileItemTypes = useMemo(
    () => fileItems.map(item => item.documentType),
    [fileItems],
  );

  const [filesBeingAdded, setFilesBeingAdded] = useState<boolean>(false);
  const [pendingContext, setPendingContext] = useState<string>("");
  const [pendingClientContextId, setPendingClientContextId] = useState<
    number | null
  >(null);
  const [
    pendingIsProjectContextDisabled,
    setPendingIsProjectContextDisabled,
  ] = useState(false);
  const [highlightedDocuments, setHighlightedDocuments] = useState<number[]>(
    [],
  );
  const [highlightNameField, setHighlightNameField] = useState<boolean>(false);
  const [showValidationError, setShowValidationError] = useState<boolean>(
    false,
  );
  const [missingMsa, setMissingMsa] = useState<boolean>(false);
  const [isAnalysing, setIsAnalysing] = useState<boolean>(false);
  const [hideUploadArea, setHideUploadArea] = useState(
    inWordAddinView && !isNew,
  );
  // When set, files are being added pursuant_to this specific tree document
  // (clicked "+"), rather than via the untargeted "Add files" flow.
  const [uploadTargetNode, setUploadTargetNode] = useState<
    MatterTreeNode | undefined
  >(undefined);
  // Existing MSA documents the lawyer marked for removal while staging a new MSA
  // (to "replace" rather than add to the current MSA). At least one MSA must remain.
  const [removedMsaIds, setRemovedMsaIds] = useState<Set<number>>(new Set());
  const previousDocIdsRef = useRef<Set<number>>(
    new Set((matter?.documents ?? []).map(doc => doc.id)),
  );
  // The banner above the upload area scrolls this into view on click, jumping the
  // user down to the tree where their upload will land.
  const treeRef = useRef<HTMLDivElement>(null);
  const scrollTreeIntoView = () =>
    treeRef.current?.scrollIntoView({behavior: "smooth", block: "start"});

  // Types addable under the targeted parent: MSA -> MSA, MSOW (if permitted),
  // SOW; MSOW -> SOW only.
  const getAllowedTypesForNode = useCallback(
    (node: MatterTreeNode | undefined): string[] | undefined => {
      if (!node) {
        return undefined;
      }
      const parentTypeId = (node.data as MatterDocument).document_type_id;
      if (parentTypeId === 1) {
        return ["MSA", ...(canUploadMsow ? ["MSOW"] : []), "SOW"];
      }
      return ["SOW"];
    },
    [canUploadMsow],
  );

  const allowedTypes = useMemo<string[] | undefined>(
    () => getAllowedTypesForNode(uploadTargetNode),
    [getAllowedTypesForNode, uploadTargetNode],
  );

  // The default type for newly-dropped files in a targeted upload, when there is
  // no ambiguity (e.g. an MSOW target only accepts SOWs).
  const targetDefaultType =
    allowedTypes && allowedTypes.length === 1 ? allowedTypes[0] : undefined;

  useEffect(() => {
    previousDocIdsRef.current = new Set(
      (matter?.documents ?? []).map(doc => doc.id),
    );
    if (inWordAddinView) {
      setHideUploadArea(Boolean(matter?.id));
    }
  }, [matter?.id]);

  useEffect(() => {
    if (inWordAddinView && currentDocumentName) {
      const currentDoc = {
        file: {name: currentDocumentName} as File,
        documentType: isNew ? undefined : "SOW",
      };

      setFileItems(prev => {
        if (prev.length > 1) {
          return prev;
        }
        return [currentDoc];
      });
    } else {
      // For web view (not word addin):
      if (
        !filesBeingAdded &&
        matter?.documents &&
        matter.documents.length > 0
      ) {
        setFileItems([]);
      }
    }
  }, [matter?.id, inWordAddinView, currentDocumentName, filesBeingAdded]);

  useEffect(() => {
    const currentDocuments = matter?.documents ?? [];
    // A replace (remove an MSA + add a new one) leaves the count unchanged, so
    // detect the upload landing by a new document id appearing instead. Iterate
    // the array rather than spreading the Set: the client Babel config
    // transpiles spread assuming arrays, so [...set] yields [set].
    const hasNewDocument = currentDocuments.some(
      doc => !previousDocIdsRef.current.has(doc.id),
    );
    const currentIds = new Set(currentDocuments.map(doc => doc.id));

    if (!inWordAddinView && filesBeingAdded && hasNewDocument) {
      setFileItems([]);
      setFilesBeingAdded(false);
      setIsAnalysing(false);
      setUploadTargetNode(undefined);
      setRemovedMsaIds(new Set());
      setCombinedTypes({SOW: false, MSOW: false});
    }

    previousDocIdsRef.current = currentIds;
  }, [matter?.documents, filesBeingAdded, inWordAddinView]);

  useEffect(() => {
    const updatedCollectionTypeNames = SUPPORTED_TYPES.reduce((acc, type) => {
      const typeHasFileItems = fileItemTypes.includes(type);
      const currentName = collectionTypeNames[type];

      if (typeHasFileItems) {
        if (currentName === null) {
          const firstDoc = fileItems.find(item => item.documentType === type);
          acc[type] = firstDoc ? firstDoc.file.name : null;
        } else {
          acc[type] = currentName;
        }
      } else {
        acc[type] = null;
      }

      return acc;
    }, {} as typeof collectionTypeNames);

    setCollectionTypeNames(updatedCollectionTypeNames);
  }, [fileItems.length, ...fileItemTypes]);

  const addFile = useCallback(
    (acceptedFiles: File[]) => {
      const newFileItems = acceptedFiles.map(file => ({
        file,
        documentType: uploadTargetNode
          ? targetDefaultType
          : isNew
          ? getNewDocumentType(file.name)
          : "SOW",
      }));
      setFileItems(prev => [...prev, ...newFileItems]);
      setShowValidationError(false);
    },
    [isNew, uploadTargetNode, targetDefaultType],
  );

  const removeFile = useCallback((index: number) => {
    setFileItems(prev => prev.filter((_, i) => i !== index));
    setShowValidationError(false);
  }, []);

  // SOW/MSOW files upload as separate documents by default; ticking a type's
  // combine toggle uploads that type's files as one document. (To flip the
  // default to combined-by-type, initialise these to true.)
  const [combinedTypes, setCombinedTypes] = useState<CombinedTypes>({
    SOW: false,
    MSOW: false,
  });

  const toggleCombineType = useCallback(
    (type: keyof CombinedTypes, combined: boolean) => {
      setCombinedTypes(prev => ({...prev, [type]: combined}));
    },
    [],
  );

  // Stamp the shared group onto combined types just before submit; the group
  // name doubles as the CombinedDocument's display name.
  const withDocumentGroups = useCallback(
    (items: FileItem[]): FileItem[] =>
      items.map(item => {
        const type = item.documentType;
        if (
          (type === "SOW" || type === "MSOW") &&
          combinedTypes[type] &&
          items.filter(other => other.documentType === type).length > 1
        ) {
          return {...item, documentGroup: collectionTypeNames[type] || type};
        }
        return {...item, documentGroup: undefined};
      }),
    [combinedTypes, collectionTypeNames],
  );

  const handleTypeChange = useCallback(
    (index: number, newType: string) => {
      setFileItems(prev => {
        const updated = prev.map((item, i) =>
          i === index
            ? {...item, documentType: newType, documentGroup: undefined}
            : item,
        );

        return updated;
      });

      setHighlightedDocuments(prev => {
        const newHighlighted = prev.filter(i => i !== index);
        if (
          newHighlighted.length === 0 &&
          name.trim() !== "" &&
          fileItems.length > 0
        ) {
          setShowValidationError(false);
        }
        return newHighlighted;
      });
    },
    [name, fileItems.length],
  );

  const handleAnalyse = async () => {
    const missingTypeIndices = fileItems.reduce<number[]>(
      (acc, item, index) => {
        if (item.documentType === undefined) {
          acc.push(index);
        }
        return acc;
      },
      [],
    );

    const isNameEmpty = name.trim() === "";

    // SOWs and MSOWs are both pursuant to an MSA, so a new family can't consist
    // of only those types - it needs at least one MSA.
    const missingMsaForNewFamily =
      isNew &&
      fileItems.length > 0 &&
      fileItems.every(item => item.documentType !== "MSA");

    const hasValidationIssues =
      isNameEmpty ||
      missingTypeIndices.length > 0 ||
      fileItems.length === 0 ||
      missingMsaForNewFamily;

    if (hasValidationIssues) {
      setHighlightNameField(isNameEmpty);
      setHighlightedDocuments(missingTypeIndices);
      setMissingMsa(missingMsaForNewFamily);
      setShowValidationError(true);
      return;
    }

    setHighlightNameField(false);
    setHighlightedDocuments([]);
    setShowValidationError(false);
    setMissingMsa(false);
    setIsAnalysing(true);

    const hasPendingContext =
      pendingContext.trim() !== "" ||
      pendingClientContextId !== null ||
      pendingIsProjectContextDisabled;

    try {
      await onAnalyse(
        name,
        withDocumentGroups(fileItems),
        collectionTypeNames,
        hasPendingContext ? pendingContext : undefined,
        pendingClientContextId,
        pendingIsProjectContextDisabled,
        effectiveTargetNode?.id,
        Array.from(removedMsaIds),
      );
    } catch {
      setIsAnalysing(false);
    }
  };

  const handleAddPursuantUpload = (node: MatterTreeNode) => {
    // Retarget the upload to the clicked document, but keep any files that have
    // already been staged so switching the target doesn't discard them.
    const newAllowedTypes = getAllowedTypesForNode(node);
    const newDefaultType =
      newAllowedTypes && newAllowedTypes.length === 1
        ? newAllowedTypes[0]
        : undefined;

    setUploadTargetNode(node);
    // A staged file's type may no longer be valid under the new target (e.g. an
    // MSOW moved under an MSOW node, which only accepts SOW). Reset any such
    // type to the new target's default (or clear it so the user re-selects).
    setFileItems(prev =>
      prev.map(item =>
        item.documentType &&
        newAllowedTypes &&
        !newAllowedTypes.includes(item.documentType)
          ? {...item, documentType: newDefaultType}
          : item,
      ),
    );
    setFilesBeingAdded(true);
    // Don't toggle the upload area's visibility here — that's controlled only by
    // the "+" next to the Analyse button (handleAddFilesWord).
    setShowValidationError(false);
    setHighlightedDocuments([]);
  };

  const handleTreeFileSelect = (document: MatterTreeNode) => {
    if (!inWordAddinView && router && params) {
      const {organisationId, projectId, matterId} = params;
      const path = `/organisation/${organisationId}/project/${projectId}/matter/${matterId}/document/${document.id}`;
      router.push(path);
    }
    if (inWordAddinView && router && params && matter) {
      const {organisationId, projectId} = params;
      const path = `/organisation/${organisationId}/project/${projectId}/matter/${matter.id}/document/${document.id}/checklist`;
      router.push(path);
    }
  };

  const handleSaveName = () => {
    if (updateName && hasNameChanged) {
      updateName(name);
    }
  };

  const handleAddFilesWord = () => {
    setHideUploadArea(!hideUploadArea);
  };

  const handleChangeCollectionTypeName = (
    collectionType: "MSA" | "SOW" | "MSOW",
    name: string | null,
  ) => {
    setCollectionTypeNames({
      ...collectionTypeNames,
      [collectionType]: name,
    });
  };

  const [onlyShowMyDocuments, setOnlyShowMyDocuments] = useState(false);

  const visibleMatter =
    onlyShowMyDocuments && currentUserId && matter
      ? {
          ...matter,
          documents: matter.documents.filter(
            doc => doc.user_id === currentUserId,
          ),
        }
      : matter;

  const addingFiles = inWordAddinView
    ? !hideFileUpload
    : isNew || filesBeingAdded;

  // While adding files, preview the staged files as pending nodes in the tree,
  // injected under the (possibly defaulted) target document.
  const pendingFileItems = addingFiles ? fileItems : [];
  const hasExistingDocuments = (matter?.documents?.length ?? 0) > 0;
  const showTree = hasExistingDocuments || pendingFileItems.length > 0;
  // The tree may need rendering before any documents exist (a new family), so
  // fall back to an empty matter shell for buildTreeStructure.
  const emptyMatter = {collections: [], documents: []} as Matter;
  const treeMatter = visibleMatter ?? emptyMatter;

  const treeStructure = useMemo(
    () => buildTreeStructure(treeMatter.documents, treeMatter.collections),
    [treeMatter.documents, treeMatter.collections],
  );
  // Without an explicit "+" target, default to the tree's first document node
  // (descending into a combined group) - targets are always documents.
  const firstDocumentNode = (
    nodes: MatterTreeNode[],
  ): MatterTreeNode | undefined => {
    for (const treeNode of nodes) {
      if (treeNode.type === "document") {
        return treeNode;
      }
      const descendant = firstDocumentNode(treeNode.children);
      if (descendant) {
        return descendant;
      }
    }
    return undefined;
  };
  const effectiveTargetNode =
    uploadTargetNode ??
    (pendingFileItems.length > 0
      ? firstDocumentNode(treeStructure)
      : undefined);

  const hasMissingTypes =
    fileItems.length > 0 && fileItems.some(item => !item.documentType);

  // A staged MSA changes what new SOWs are analysed against; flag it while
  // staging.
  const hasStagedMsa = fileItems.some(item => item.documentType === "MSA");

  const handleRemoveMsaFile = useCallback((node: MatterTreeNode) => {
    setRemovedMsaIds(prev => {
      const next = new Set(prev);
      if (next.has(node.id)) {
        next.delete(node.id);
      } else {
        next.add(node.id);
      }
      return next;
    });
  }, []);

  // At least one MSA must remain, so the last one can't be removed.
  const remainingMsaCount =
    (matter?.documents ?? []).filter(
      doc => doc.document_type_id === 1 && !removedMsaIds.has(doc.id),
    ).length + fileItems.filter(item => item.documentType === "MSA").length;
  const canRemoveMsa = remainingMsaCount > 1;

  const validationMessage = (() => {
    if (!showValidationError) {
      return "";
    }

    const issues = [
      name.trim() === "" && "Name is required",
      fileItems.length === 0 && "At least one file is required",
      hasMissingTypes && "All documents must have a type selected",
      missingMsa && "At least one MSA is required",
    ].filter(Boolean);

    return issues.join(", ");
  })();

  return (
    <div
      className={inWordAddinView ? "" : "app-container"}
      style={
        inWordAddinView ? {} : {flexDirection: "column", alignItems: "center"}
      }
    >
      <div style={inWordAddinView ? {} : {maxWidth: "900px", width: "stretch"}}>
        {!inWordAddinView && (
          <Toolbar
            icon={<Description />}
            title={
              <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                <span>Contract Family Editor</span>
                <Tooltip title="Upload and organise your MSAs and SOWs for analysis. Group related documents together to analyse contracts and their dependencies.">
                  <IconButton size="small" style={{padding: "4px"}}>
                    <InfoOutlined
                      style={{fontSize: "1.2rem", color: "#757575"}}
                    />
                  </IconButton>
                </Tooltip>
              </div>
            }
            rightChildren={
              <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                {matter?.id && updateContext && canEditContext && (
                  <MatterContextButton
                    organisationId={parseInt(params.organisationId, 10)}
                    context={matter.context ?? ""}
                    clientContextId={matter.client_context_id}
                    isProjectContextDisabled={
                      matter.is_project_context_disabled ?? false
                    }
                    onSave={updateContext}
                  />
                )}
                {!matter?.id && canEditContext && (
                  <MatterContextButton
                    organisationId={parseInt(params.organisationId, 10)}
                    context={pendingContext}
                    clientContextId={pendingClientContextId}
                    isProjectContextDisabled={pendingIsProjectContextDisabled}
                    onSave={(
                      newContext,
                      clientContextId,
                      isProjectContextDisabled,
                    ) => {
                      setPendingContext(newContext);
                      setPendingClientContextId(clientContextId);
                      setPendingIsProjectContextDisabled(
                        Boolean(isProjectContextDisabled),
                      );
                    }}
                  />
                )}
                {!isNew && !addingFiles ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setFilesBeingAdded(true);
                      setFileItems([]);
                      setCombinedTypes({SOW: false, MSOW: false});
                    }}
                  >
                    Add files
                  </Button>
                ) : null}
              </div>
            }
          />
        )}
        {!hideNameField && (
          <div style={styles.nameFieldContainer}>
            <TextField
              label="Name"
              variant="outlined"
              size={inWordAddinView ? "small" : "medium"}
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (e.target.value.trim() !== "") {
                  setHighlightNameField(false);
                  if (
                    highlightedDocuments.length === 0 &&
                    fileItems.length > 0
                  ) {
                    setShowValidationError(false);
                  }
                }
              }}
              style={{width: inWordAddinView ? "100%" : "30em"}}
              error={highlightNameField}
              placeholder={isNew ? "e.g. Acme Ltd - IT Services Renewal" : ""}
              InputLabelProps={isNew ? {shrink: true} : undefined}
              helperText={highlightNameField ? "Name is required" : ""}
            />
            {hasNameChanged && !isNew && (
              <Button
                color="primary"
                variant="contained"
                onClick={handleSaveName}
                style={{height: "56px"}}
              >
                Save
              </Button>
            )}
            {!inWordAddinView && currentUserId && (
              <IconToggle
                Icon={AccountCircleIcon}
                tooltip="Only show my documents"
                enabled={onlyShowMyDocuments}
                onToggle={() => setOnlyShowMyDocuments(v => !v)}
              />
            )}
          </div>
        )}
        {!hideNameField && isNew && !highlightNameField && (
          <div style={styles.nameFormatHint}>
            <span style={styles.nameFormatLabel}>Suggested format</span>
            <span style={styles.nameFormatValue}>
              [Master Client Name] <span style={{color: "#9e9e9e"}}>-</span>{" "}
              [Parent document name]
            </span>
          </div>
        )}
        {!hideNameField && !matter?.id && inWordAddinView && canEditContext && (
          <div style={{marginBottom: ".5em"}}>
            <MatterContextButton
              organisationId={parseInt(params.organisationId, 10)}
              context={pendingContext}
              clientContextId={pendingClientContextId}
              isProjectContextDisabled={pendingIsProjectContextDisabled}
              onSave={(
                newContext,
                clientContextId,
                isProjectContextDisabled,
              ) => {
                setPendingContext(newContext);
                setPendingClientContextId(clientContextId);
                setPendingIsProjectContextDisabled(
                  Boolean(isProjectContextDisabled),
                );
              }}
              variant="row"
            />
          </div>
        )}
        {addingFiles && (
          <>
            {effectiveTargetNode && (
              <div
                style={styles.targetBanner}
                onClick={scrollTreeIntoView}
                role="button"
                tabIndex={0}
              >
                <span>
                  Adding files under <strong>{effectiveTargetNode.name}</strong>
                </span>
                <span style={styles.targetBannerHint}>
                  View in tree
                  <ExpandMore style={{fontSize: "1.1rem"}} />
                </span>
              </div>
            )}
            {!isNew && hasStagedMsa && (
              <div style={styles.msaBanner}>
                <InfoOutlined style={{fontSize: "1.1rem"}} />
                {removedMsaIds.size > 0 ? (
                  <span>
                    <strong>Replacing the MSA:</strong> new SOWs are analysed
                    against the new MSA. Existing SOWs are marked as reviewed
                    against the previous one.
                  </span>
                ) : (
                  <span>
                    <strong>Adding to the MSA:</strong> all MSA files are
                    re-analysed as one agreement. Existing SOWs are marked as
                    reviewed against the previous version.
                  </span>
                )}
              </div>
            )}
            <FileUploader
              fileItems={fileItems}
              addFile={addFile}
              removeFile={removeFile}
              handleTypeChange={handleTypeChange}
              combinedTypes={combinedTypes}
              toggleCombineType={toggleCombineType}
              hideUploadArea={hideUploadArea}
              inWordAddin={inWordAddinView}
              highlightedDocuments={highlightedDocuments}
              collectionTypeNames={collectionTypeNames}
              setCollectionTypeNames={handleChangeCollectionTypeName}
              canUploadMsow={canUploadMsow}
              allowedTypes={allowedTypes}
            />
            <div style={styles.analyseButtonContainer}>
              <div>
                <Button
                  variant="contained"
                  onClick={handleAnalyse}
                  disabled={isAnalysing}
                >
                  {isAnalysing ? "Analysing..." : "Analyse"}
                </Button>
                {inWordAddinView && !isNew && (
                  <Tooltip title="Add more SOW Documents">
                    <IconButton aria-label="add" onClick={handleAddFilesWord}>
                      {hideUploadArea ? <Add /> : <Close />}
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              {hasMissingTypes && !showValidationError && (
                <div style={{...styles.messageBase, ...styles.infoMessage}}>
                  Select a type for each document to enable analysis
                </div>
              )}
              {showValidationError && validationMessage && (
                <div style={{...styles.messageBase, ...styles.errorMessage}}>
                  {validationMessage}
                </div>
              )}
            </div>
          </>
        )}

        {showTree ? (
          <div ref={treeRef}>
            <MatterTreeView
              matterData={treeMatter}
              onNodeSelect={handleTreeFileSelect}
              onAddPursuantUpload={
                inWordAddinView || addingFiles
                  ? handleAddPursuantUpload
                  : undefined
              }
              canUploadMsow={canUploadMsow}
              uploadTargetNodeId={effectiveTargetNode?.id}
              isAddingFiles={addingFiles}
              pendingUpload={
                pendingFileItems.length > 0
                  ? {
                      targetNodeId: effectiveTargetNode?.id,
                      fileItems: pendingFileItems,
                    }
                  : undefined
              }
              onRemoveMsaFile={handleRemoveMsaFile}
              removedMsaIds={removedMsaIds}
              canRemoveMsa={canRemoveMsa}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MatterEditor;
